import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as AddIcon } from '@/assets/icons/add-bordered.svg';
import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar.svg';
import { ReactComponent as InfoIcon } from '@/assets/icons/info-icon.svg';
import { ReactComponent as MultilangIcon } from '@/assets/icons/multilang.svg';
import noChannelImg from '@/assets/icons/no-channel-placeholder.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import reelsThumbPlaceholder from '@/assets/icons/reels-thumb-placeholder.svg';
import { ReactComponent as ReservedIcon } from '@/assets/icons/reserved.svg';
import thumbnailPlaceholder from '@/assets/icons/thumbnail-placeholder.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import NetworkIcon from '@/components/NetworkIcon';
import { Thumbnail } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { ReleaseResource } from '@/types/resources';
import { api } from '@/utils/api';
import { ReleaseStatusEnum } from '@/utils/enums';
import { numFormatter } from '@/utils/helpers';

import ConditionalRepostBadge from './ConditionalRepostBadge';
import ConditionalRepostsButton from './ConditionalRepostsButton';
import {
  Description,
  DescriptionText,
  ImageWrap,
} from './DistributionCalendarModalCard';
import DistributionListItemConditions from './DistributionListItemConditions';
import DistributionRepostsForm from './DistributionRepostsForm';
import DistributionScheduleDetailsModal from './DistributionScheduleDetailsModal';
import DistributionScheduleModal from './DistributionScheduleModal';
import DistributionStatusBadge from './DistributionStatusBadge';
import DistributionTypeBadge from './DistributionTypeBadge';
import EditReleaseInformationForm from './EditReleaseInformationForm';
import ExclusivityBadge from './ExclusivityBadge';
import ReleaseTypeBadge from './ReleaseTypeBadge';
import ThirdPartyBadge from './ThirdPartyBadge';

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px 24px;
  margin-bottom: 16px;
`;

const BoxTop = styled.div`
  display: flex;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.lightGray};
  margin: 0 -24px;
  padding: 0 24px 16px;
  position: relative;
`;

const ActionsDropdownWrap = styled.div`
  position: absolute;
  top: 0;
  right: 12px;
`;

const BoxBottom = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 16px;
`;

const BoxMiddle = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  margin: 0 -24px;
  align-items: center;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.lightGray};
`;

const BoxTopLeft = styled.div`
  width: 50%;
  display: flex;
  padding-right: 30px;
`;

const BoxTopRight = styled.div`
  width: 50%;
  display: flex;
  padding-left: 22px;
  justify-content: flex-end;
`;

const BoxTopColumn = styled.div`
  width: 33.33%;
  &:last-child {
    padding-right: 16px;
  }
`;

const BoxBottomLeft = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Image = styled.img`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(144, 144, 144, 0.3);
`;

const BoxBottomRight = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const ColumnTitle = styled(Text)`
  margin-bottom: 2px;
`;

export const Time = styled(Text)`
  display: inline-block;
  background: ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  font-size: 11px;
  padding: 6px 12px 5px;
  min-width: 52px;
  text-align: center;
  min-width: 54px;
  > span {
    font-weight: 400;
  }
`;

const StyledThumbnail = styled(Thumbnail)`
  height: 49px;
  width: 89px;
  flex-shrink: 0;
`;

const Title = styled(Text)`
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  > span {
    margin-bottom: 6px;
  }
`;

const LanguageTitle = styled(Text).attrs({ $type: 'smallNormal' })`
  margin-top: auto;
  position: relative;
  padding-left: 12px;
  &::before {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto 0;
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.warning};
  }
`;

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 200px;
  z-index: 15;
`;

const MultilangBadge = styled(Text).attrs({
  $type: 'smallHeadline',
  as: 'div',
})`
  border-radius: 4px;
  height: 27px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  background: ${({ theme }) => theme.colors.lightGray};
`;

const ChannelGroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    position: static !important;
  }
`;

const SecondaryBadge = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray};
  gap: 8px;
  border-radius: 4px;
  height: 27px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  padding: 0 12px;
`;

const BoxBottomLeftDivider = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.lightGray};
`;

interface Props {
  release: ReleaseResource;
  showRepostsDetails?: boolean;
}

const DistributionListItem: FC<Props> = ({
  release,
  showRepostsDetails = false,
}) => {
  const [showScheduleUpdateModal, setShowScheduleUpdateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRepostsModal, setShowRepostsModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete?`,
        release.title,
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(`/v1/releases/${release.id}`);

      queryClient.invalidateQueries(['releases'], {
        exact: false,
      });

      alerts.success('Schedule was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const isFullyEditable = [
    ReleaseStatusEnum.DRAFT,
    ReleaseStatusEnum.PREPARED,
    ReleaseStatusEnum.REJECTED,
  ].includes(release.status);

  const renderAudienceSegmentationCondition = () => {
    const { conditions } = release;

    if (conditions?.female_percentage) {
      return `Female: ${conditions?.female_percentage}%`;
    }

    if (conditions?.male_percentage) {
      return `Female: ${conditions?.male_percentage}%`;
    }

    return '-';
  };

  const thumbnailSrc = release.isReel
    ? reelsThumbPlaceholder
    : release.thumbnail?.versions?.preview ??
      release.thumbnail?.versions?.original ??
      thumbnailPlaceholder;

  return (
    <>
      <Box>
        <BoxTop>
          <ActionsDropdownWrap>
            <StyledActionsDropdown>
              {release.status === ReleaseStatusEnum.POSTED &&
                !release.is_repost &&
                !release.isReel && (
                  <ActionsDropdownButton
                    onClick={() => setShowRepostsModal(true)}
                    as="button"
                  >
                    <span>
                      <AddIcon />
                    </span>
                    Add conditional repost
                  </ActionsDropdownButton>
                )}
              {release.status !== ReleaseStatusEnum.DRAFT &&
                release.status !== ReleaseStatusEnum.PREPARED && (
                  <ActionsDropdownButton
                    onClick={() => setShowDetailsModal(true)}
                    as="button"
                  >
                    <span>
                      <InfoIcon />
                    </span>
                    Details
                  </ActionsDropdownButton>
                )}
              <ActionsDropdownButton
                onClick={() => setShowScheduleUpdateModal(true)}
                as="button"
              >
                <span>
                  {release.status === ReleaseStatusEnum.REJECTED ? (
                    <CalendarIcon />
                  ) : (
                    <PencilIcon />
                  )}
                </span>
                {release.status === ReleaseStatusEnum.REJECTED
                  ? 'Reschedule'
                  : 'Edit'}
              </ActionsDropdownButton>
              <ActionsDropdownButton onClick={onDelete} as="button">
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </StyledActionsDropdown>
          </ActionsDropdownWrap>
          <BoxTopLeft>
            <StyledThumbnail src={thumbnailSrc} />
            <Title $type="smallBold">
              <span>{release.title}</span>
              {!!release.mlTranslation && (
                <>
                  <LanguageTitle as="div">
                    {release.mlTranslation.language}
                  </LanguageTitle>
                </>
              )}
            </Title>
          </BoxTopLeft>
          <BoxTopRight>
            <BoxTopColumn>
              <ColumnTitle $type="smallBold">Editor</ColumnTitle>
              <Text $type="smallMedium" $color="gray">
                {release.employees?.find((e) => e.release_role === 'editor')
                  ?.name ?? '-'}
              </Text>
            </BoxTopColumn>
            <BoxTopColumn>
              <ColumnTitle $type="smallBold">Fresh ID</ColumnTitle>
              <Text $type="smallMedium" $color="gray">
                {release.freshId ?? '-'}
              </Text>
            </BoxTopColumn>
            <BoxTopColumn>
              <ColumnTitle $type="smallBold">Project ID</ColumnTitle>
              <Text $type="smallMedium" $color="gray">
                {release.projectId}
              </Text>
            </BoxTopColumn>
            {!!release.author && (
              <BoxTopColumn>
                <ColumnTitle $type="smallBold">Set Up By</ColumnTitle>
                <Text $type="smallMedium" $color="gray">
                  {release.author}
                </Text>
              </BoxTopColumn>
            )}
          </BoxTopRight>
        </BoxTop>
        {release.is_repost && (
          <BoxMiddle>
            <ConditionalRepostBadge hasConditions={!!release.conditions} />
            {!!release.conditions && (
              <BoxTopRight>
                <BoxTopColumn>
                  <ColumnTitle $type="smallBold">Target Views</ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {release.conditions.views
                      ? numFormatter(parseInt(release.conditions.views))
                      : '-'}
                  </Text>
                </BoxTopColumn>
                <BoxTopColumn>
                  <ColumnTitle $type="smallBold">Target Days</ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {release.conditions.period ?? '-'}
                  </Text>
                </BoxTopColumn>
                <BoxTopColumn>
                  <ColumnTitle $type="smallBold">Target Audience</ColumnTitle>
                  <Text $type="smallMedium" $color="gray">
                    {renderAudienceSegmentationCondition()}
                  </Text>
                </BoxTopColumn>
              </BoxTopRight>
            )}
          </BoxMiddle>
        )}
        <BoxBottom>
          <BoxBottomLeft>
            <Description>
              {!!release.channelGroup &&
              release.status !== ReleaseStatusEnum.WAITING ? (
                <ChannelGroupTitle>
                  <svg
                    width="27"
                    height="27"
                    viewBox="0 0 27 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="0.5"
                      y="0.5"
                      width="26"
                      height="26"
                      rx="3.5"
                      fill="#F8F8F8"
                      stroke="#E9E9E9"
                    />
                    <path
                      d="M17.5017 10.0714H7.21596C6.90037 10.0714 6.64453 10.3272 6.64453 10.6428V18.6428C6.64453 18.9584 6.90037 19.2143 7.21596 19.2143H17.5017C17.8173 19.2143 18.0731 18.9584 18.0731 18.6428V10.6428C18.0731 10.3272 17.8173 10.0714 17.5017 10.0714Z"
                      stroke="#909090"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.92969 10.0715V8.3572C8.92969 8.20564 8.98989 8.0603 9.09705 7.95313C9.20422 7.84597 9.34956 7.78577 9.50112 7.78577H19.7868C19.9384 7.78577 20.0837 7.84597 20.1909 7.95313C20.2981 8.0603 20.3583 8.20564 20.3583 8.3572V16.3572C20.3583 16.5087 20.2981 16.6541 20.1909 16.7613C20.0837 16.8684 19.9384 16.9286 19.7868 16.9286H18.0725"
                      stroke="#909090"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M6.64453 12.3572H18.0731"
                      stroke="#909090"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <Text $type="smallHeadline">{release.channelGroup.name}</Text>
                </ChannelGroupTitle>
              ) : (
                <>
                  {!!release.channel?.thumbnail ? (
                    <>
                      <ImageWrap>
                        <Image
                          src={release.channel.thumbnail}
                          alt={release.channel.name + ' thumbnail'}
                        />
                        <NetworkIcon type={release.channel.network} />
                      </ImageWrap>
                      <DescriptionText>
                        <Text $type="smallHeadline">
                          {release.channel.name}
                        </Text>
                        {!!release.channel.tags?.length && (
                          <Text $type="extraSmall" $color="gray">
                            {release.channel.tags.map((t) => t.title).join(',')}
                          </Text>
                        )}
                      </DescriptionText>
                    </>
                  ) : (
                    <>
                      <ImageWrap>
                        <Image
                          src={noChannelImg}
                          alt={'Channel icon placeholder'}
                        />
                      </ImageWrap>
                      <DescriptionText>
                        <Text $type="smallHeadline" $color="gray">
                          No channel
                        </Text>
                      </DescriptionText>
                    </>
                  )}
                </>
              )}
            </Description>
            <ReleaseTypeBadge
              isReel={release.isReel}
              videoUrl={release.video?.versions.original}
            />
            {!!release.isReserved && (
              <SecondaryBadge>
                <ReservedIcon />
                <Text $type="smallHeadline">Reserved</Text>
              </SecondaryBadge>
            )}
            <BoxBottomLeftDivider />
            {!!release.mlTranslation && (
              <MultilangBadge>
                <MultilangIcon />
                Multilanguage
              </MultilangBadge>
            )}
            <DistributionTypeBadge type={release.compilationType} />
            {!!release.is3rdParty && (
              <>
                <ThirdPartyBadge />
                <ExclusivityBadge isExclusive={release.isExclusive} />
              </>
            )}
            {!!release.repostsCount && (
              <ConditionalRepostsButton releaseId={release.id} />
            )}
          </BoxBottomLeft>
          <BoxBottomRight>
            {!!release.releasedAt && (
              <Time $type="smallBold">
                {isFullyEditable ? (
                  <>
                    {DateTime.fromISO(release.releasedAt).toFormat('dd/MM/y')}
                    {' · '}
                    <span>
                      {DateTime.fromISO(release.releasedAt).toFormat('HH:mm')}
                    </span>
                  </>
                ) : (
                  DateTime.fromISO(release.releasedAt).toFormat('HH:mm')
                )}
              </Time>
            )}
            <DistributionStatusBadge status={release.status} />
          </BoxBottomRight>
        </BoxBottom>
        {showRepostsDetails && (
          <DistributionListItemConditions reposts={release.reposts!} />
        )}
      </Box>
      <AnimatePresence>
        {showScheduleUpdateModal &&
          (isFullyEditable ? (
            <DistributionScheduleModal
              editableRelease={release}
              onClose={() => setShowScheduleUpdateModal(false)}
            />
          ) : (
            <EditReleaseInformationForm
              editableRelease={release}
              onClose={() => setShowScheduleUpdateModal(false)}
            />
          ))}
        {showDetailsModal && (
          <DistributionScheduleDetailsModal
            releaseId={release.id}
            onClose={() => setShowDetailsModal(false)}
          />
        )}
        {showRepostsModal && (
          <DistributionRepostsForm
            release={release}
            onClose={() => setShowRepostsModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default DistributionListItem;
